import config from "@/config";
import Axios from "axios";
import { useAuthStore } from "./stores/auth";

const isInThePast = (epochMillis: number): boolean => {
  const nowMillis = new Date().getTime();
  return nowMillis > epochMillis;
};

export const ensureRoleSet = async () => {
  const auth = useAuthStore();
  if (auth.role) {
    return;
  }

  const r = await Axios.post(`${config.apiHost}/admin/role`, undefined, {
    validateStatus: null,
    headers: {
      Authorization: "Bearer " + localStorage.getItem("pn-access-token"),
      "Content-Type": "application/json; charset=UTF-8",
    },
  });
  if (r.status === 200) {
    auth.setRole(r.data);
  } else {
    auth.setRole({ email: "", role: "", active: false, lastLogin: "" });
  }
};

export const refreshToken = async (): Promise<boolean> => {
  const rtoken = localStorage.getItem("pn-refresh-token");
  if (!rtoken) {
    return false;
  }
  console.log("refreshing token");
  const body = new URLSearchParams();
  body.append("grant_type", "refresh_token");
  body.append("refresh_token", rtoken);
  body.append("client_id", config.clientId);
  const response = await Axios.post(config.tokenUrl, body, {
    validateStatus: null,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });

  if (response.status >= 400) {
    console.log("400 response, relogin");
    localStorage.removeItem("pn-refresh-token");
    return false;
  }

  if (!response.data.access_token) {
    localStorage.removeItem("pn-refresh-token");
    throw new Error("no access token received");
  }
  if (!response.data.refresh_token) {
    localStorage.removeItem("pn-refresh-token");
    console.log("no refresh token received");
    return false;
  }

  setPnToken(response.data);

  return true;
};

export const isLoggedIn = (): boolean => {
  if (!localStorage.getItem("pn-access-token")) {
    return false;
  }
  const autoLogoutTime = localStorage.getItem("auto-logout-time");
  if (!autoLogoutTime) {
    return false;
  }
  const tokenExpireMillis = parseInt(autoLogoutTime, 10);
  if (isInThePast(tokenExpireMillis)) {
    return false;
  }
  return true;
};

export const setPnToken = (tokenData: {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}) => {
  localStorage.setItem("pn-access-token", tokenData.access_token);
  if (tokenData.refresh_token) {
    localStorage.setItem("pn-refresh-token", tokenData.refresh_token);
  }
  const autoLogoutInMilliseconds = (tokenData.expires_in - 60) * 1000; // 1 minute before token expires
  const nowMilliseconds = new Date().getTime();
  localStorage.setItem(
    "auto-logout-time",
    (nowMilliseconds + autoLogoutInMilliseconds).toString(),
  );
};

export const authenticateLogin = async (code: string) => {
  console.log("authenticating login");
  if (!code) {
    throw new Error("missing code in auth");
  }
  const verifier = localStorage.getItem("code-verifier");
  if (!verifier) {
    throw new Error("missing code verifier in auth");
  }

  const body = new URLSearchParams();
  body.append("grant_type", "authorization_code");
  body.append("client_id", config.clientId);
  body.append("code", code);
  body.append("redirect_uri", config.redirectUri);
  body.append("code_verifier", verifier);
  console.log(`posting to: ${config.tokenUrl}`);

  const response = await Axios.post(config.tokenUrl, body, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
  // todo check response for 400 grant invalid
  setPnToken(response.data);
  localStorage.removeItem("code-verifier");
};
